import { useState } from "react";
import { Btn, Modal } from "components/ui/index";
import { MONTHS_FULL, MONTHS_SHORT } from "constants/index";
import { T } from "theme/index";
import { fmtIDR, fmtNum, fmtShort, parseNum } from "utils/format";

// ── Budget Modal ──────────────────────────────────────────────────────────────
export default function BudgetModal({ existing, allBudgets, categories, wallets, month, year, actualIncome, onSave, onClose }) {
  const [tab, setTab] = useState("expense");
  const budgetCats = categories.filter(c => c.type === "expense" || c.type === "investasi");

  // Budgets bulan lalu untuk fitur "salin"
  const prevMonth   = month === 0 ? 11 : month - 1;
  const prevYear    = month === 0 ? year - 1 : year;
  const prevBudgets = (allBudgets||[]).filter(b => b.month===prevMonth && b.year===prevYear);
  const hasPrev     = prevBudgets.length > 0;

  const [amounts,   setAmounts]   = useState(() => {
    const init = {};
    budgetCats.forEach(c => { const b = existing.find(b => b.categoryId===c.id); init[c.id] = b ? String(b.amount) : ""; });
    return init;
  });
  const [walletMap, setWalletMap] = useState(() => {
    const init = {};
    budgetCats.forEach(c => { const b = existing.find(b => b.categoryId===c.id); init[c.id] = b?.walletId || ""; });
    return init;
  });

  const copyFromPrev = () => {
    if (!hasPrev) return;
    const newAmounts   = { ...amounts };
    const newWalletMap = { ...walletMap };
    prevBudgets.forEach(b => {
      newAmounts[b.categoryId]   = String(b.amount);
      if (b.walletId) newWalletMap[b.categoryId] = b.walletId;
    });
    setAmounts(newAmounts);
    setWalletMap(newWalletMap);
  };

  const save = () => {
    const result = budgetCats.filter(c => amounts[c.id]).map(c => ({ categoryId:c.id, amount:parseNum(amounts[c.id]), walletId:walletMap[c.id]||"", month, year }));
    onSave(result); onClose();
  };

  // Live tracker — hitung total anggaran semua kategori (expense + investasi)
  const totalExp = budgetCats.filter(c=>c.type==="expense").reduce((a,c)=>a+parseNum(amounts[c.id]||0),0);
  const totalInv = budgetCats.filter(c=>c.type==="investasi").reduce((a,c)=>a+parseNum(amounts[c.id]||0),0);
  const totalBudgeted = totalExp + totalInv;
  const sisa = actualIncome - totalBudgeted;
  const pctUsed = actualIncome > 0 ? Math.min(totalBudgeted / actualIncome, 1) : 0;
  const isOver = totalBudgeted > actualIncome;

  const shown = budgetCats.filter(c => c.type === tab);
  return (
    <Modal title={`Anggaran — ${MONTHS_SHORT[month]} ${year}`} onClose={onClose} wide>

      {/* Salin dari bulan lalu */}
      {hasPrev && (
        <button onClick={copyFromPrev}
          style={{ width:"100%", background:T.blue+"15", border:`1px solid ${T.blue}33`, borderRadius:9, color:T.blue, cursor:"pointer", padding:"10px 0", fontSize:13, fontWeight:700, marginBottom:12, display:"flex", alignItems:"center", justifyContent:"center", gap:7, WebkitTapHighlightColor:"transparent" }}>
          📋 Salin Anggaran dari {MONTHS_FULL[prevMonth]} {prevYear}
        </button>
      )}
      {actualIncome > 0 && (
        <div style={{ background:T.surface, border:`1px solid ${isOver?T.red+"44":T.border}`, borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:6 }}>
            <div style={{ fontSize:11, color:T.muted, fontWeight:600 }}>INCOME {MONTHS_SHORT[month]}</div>
            <div style={{ fontFamily:"monospace", fontWeight:800, color:T.green, fontSize:14 }}>{fmtIDR(actualIncome)}</div>
          </div>
          {/* Progress bar */}
          <div style={{ height:8, background:T.border, borderRadius:4, marginBottom:8, overflow:"hidden" }}>
            <div style={{ height:"100%", width:"100%", borderRadius:4, transformOrigin:"left", transform:`scaleX(${Math.min(pctUsed,1)})`, background:isOver?T.red:pctUsed>0.9?T.orange:T.gold, transition:"transform 0.2s" }} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6 }}>
            {[
              ["💸 Pengeluaran", totalExp,       T.red],
              ["📈 Investasi",   totalInv,        T.gold],
              [isOver?"⚠️ Over Budget":"✅ Sisa", sisa, isOver?T.red:T.green],
            ].map(([label, val, col]) => (
              <div key={label} style={{ background:T.card, borderRadius:7, padding:"7px 10px", border:`1px solid ${col}22` }}>
                <div style={{ color:T.muted, fontSize:9, fontWeight:600, marginBottom:3 }}>{label}</div>
                <div style={{ color:col, fontFamily:"monospace", fontWeight:700, fontSize:12 }}>{fmtShort(Math.abs(val))}</div>
              </div>
            ))}
          </div>
          {isOver && <div style={{ color:T.red, fontSize:11, marginTop:8, fontWeight:600 }}>⚠️ Total anggaran melebihi income {fmtShort(Math.abs(sisa))}</div>}
        </div>
      )}

      {/* Tab pengeluaran / investasi */}
      <div style={{ display:"flex", background:T.surface, borderRadius:9, padding:3, marginBottom:14, border:`1px solid ${T.border}` }}>
        {[["expense","💸 Pengeluaran"],["investasi","📈 Investasi"]].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)} style={{ flex:1, padding:"8px 0", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, border:"none", background:tab===v?T.card:T.surface, color:tab===v?T.text:T.muted, WebkitTapHighlightColor:"transparent" }}>{l}</button>
        ))}
      </div>
      <div style={{ maxHeight:360, overflow:"auto", paddingRight:4 }}>
        {shown.map(cat => (
          <div key={cat.id} style={{ marginBottom:12 }}>
            {/* Baris 1: ikon + nama */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:17, width:22, textAlign:"center", flexShrink:0 }}>{cat.icon}</span>
              <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>{cat.name}</div>
            </div>
            {/* Baris 2: rekening + nominal (selalu sejajar) */}
            <div style={{ display:"flex", gap:8, paddingLeft:30 }}>
              {wallets.length > 0 && (
                <select value={walletMap[cat.id]||""} onChange={e => setWalletMap(m => ({ ...m, [cat.id]:e.target.value }))}
                  style={{ flex:1, minWidth:0, background:T.surface, border:`1px solid ${T.border}`, borderRadius:7, color:T.muted, fontSize:12, padding:"8px 8px", cursor:"pointer", outline:"none" }}>
                  <option value="">— Rekening —</option>
                  {wallets.map(w => <option key={w.id} value={w.id}>{w.icon} {w.name}</option>)}
                </select>
              )}
              <input type="text" inputMode="numeric" value={fmtNum(amounts[cat.id])} onChange={e => setAmounts(a => ({ ...a, [cat.id]:e.target.value.replace(/\D/g,"") }))} placeholder="0"
                style={{ width:110, flexShrink:0, background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:14, padding:"8px 10px", outline:"none", textAlign:"right" }}
              />
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <div style={{ padding:"20px 0", textAlign:"center", color:T.muted, fontSize:12 }}>
            Belum ada kategori tipe ini.<br/>Tambahkan dulu di menu Kategori.
          </div>
        )}
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:14 }}>
        <Btn variant="ghost" onClick={onClose}>Batal</Btn>
        <Btn onClick={save}>💾 Simpan</Btn>
      </div>
    </Modal>
  );
}
